import { useCallback, useEffect, useState } from 'react';

const isEventObject = (event) => {
	return typeof event === 'object' && 'target' in event;
};

export const useFormHelper = ({ defaultValues, validators, validateOn = 'none' }) => {
	const [data, setData] = useState(defaultValues);
	const [errors, setErrors] = useState({});

	const validateField = useCallback((name, value, validator) => {
		const newErrors = { ...errors };
		delete newErrors[name];

		if (validator) {
			const violations = validator(value);
			if (violations.length > 0) {
				newErrors[name] = violations;
			}
		}

		setErrors(newErrors);
	}, [errors, setErrors]);

	const validate = useCallback(() => {
		const newErrors = {};

		for (const key in validators) {
			const validator = validators[key] ?? null;

			if (validator) {
				const errorMessages = validator(data[key]);

				if (errorMessages.length > 0) {
					newErrors[key] = errorMessages;
				}
			}
		}

		setErrors(newErrors);
		return Object.values(newErrors).length === 0;
	}, [setErrors, validators, data]);

	const hasFieldErrors = useCallback((name) => {
		return name in errors && errors[name].length > 0;
	}, [errors]);

	const onChange = useCallback((event) => {
		if (!isEventObject(event)) {
			return;
		}

		const name = event.target.name;
		const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;

		if (validateOn === 'change') {
			validateField(name, value, validators[name] ?? undefined);
		}

		setData({ ...data, [name]: value });
	}, [validateField, validateOn, validators, data]);

	const onBlur = useCallback((event) => {
		if (!isEventObject(event)) {
			return;
		}

		if (validateOn === 'blur') {
			const name = event.target.name;
			validateField(name, data[name], validators[name] ?? undefined);
		}
	}, [validateField, validateOn, data, validators]);

	const reset = useCallback(() => {
		setData(defaultValues);
		setErrors({});
	}, [defaultValues]);

	useEffect(() => {
		const onBeforeUnload = (event) => {
			if (JSON.stringify(defaultValues) !== JSON.stringify(data)) {
				event.preventDefault();
				event.returnValue = true;
			}
		};

		window.addEventListener('beforeunload', onBeforeUnload);

		return () => {
			window.removeEventListener('beforeunload', onBeforeUnload);
		};
	});

	return {
		data,
		errors,
		reset,
		onChange,
		onBlur,
		validate,
		hasErrors: Object.keys(errors).length > 0,
		hasFieldErrors,
	}
};
